import {
  ArrowRightLeft,
  BadgePercent,
  BookOpen,
  BookOpenText,
  Box,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  GraduationCap,
  ListTodo,
  LogOut,
  MessageSquareText,
  MessagesSquare,
  Settings,
  Users2,
  Wallet2,
} from "lucide-react";
import { Link, NavLink } from "react-router-dom";
import { HashLink, NavHashLink, NavNavHashLink } from "react-router-hash-link";
import { useAPI } from "../context/APIContext";
import { useLanguage } from "../context/LanguageContext";
import { SidebarData } from "../lib/languages";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "./ui/sidebar";
import { useState } from "react";
import Cookie from "js-cookie";
import { useAuth } from "../context/AuthContext";

const LogoSideBar = ({ logoName }) => (
  <SidebarMenu>
    <SidebarMenuItem>
      <SidebarMenuButton
        size="lg"
        className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
      >
        <Link to={"/dashboard"} className="flex items-center gap-2">
          <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
            <BookOpen className="size-4" />
          </div>
          <span className="truncate font-semibold">{logoName}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  </SidebarMenu>
);

export function AppSidebarAdmin() {
  const { logout } = useAuth();
  const { language } = useLanguage();
  const t = SidebarData[language] || SidebarData["en"];

  const [storeName, setStoreName] = useState("");

  const { categoriesItems, subCategoriesItems, coursesItems } = useAPI();
  const { categories, loadingCategories } = categoriesItems;
  const { loadingSubCategory, subCategories } = subCategoriesItems;
  const { loadingCourses, courses } = coursesItems;

  const ArrowIcon = language === "ar" ? ChevronLeft : ChevronRight;

  return (
    <Sidebar
      variant={"floating"}
      collapsible={"icon"}
      side={language === "ar" ? "right" : "left"}
      dir={language === "ar" ? "rtl" : "ltr"}
    >
      <SidebarHeader>
        <LogoSideBar logoName={t.logoName} />
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <MenuItemComponent
                Icon={BookOpen}
                url={"/admin/overview"}
                title={t.overview}
                setStoreName={setStoreName}
              />
              {/* ------------------------------------ */}
              {/* <MenuItemComponent
                Icon={Users2}
                url={"/admin/teachers"}
                title={t.teacherManagement}
                setStoreName={setStoreName}
              /> */}
              {/* ------------------------------------ */}

              {/* ----------Categories----------- */}
              <Collapsible asChild className="group/collapsible">
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip={t.categories}>
                      <NavLink
                        to={"/admin/categories"}
                        className={"flex items-center gap-2 !bg-transparent"}
                        onClick={() => setStoreName("Categories")}
                      >
                        <Box className="size-4" />
                        <span>{t.categories}</span>
                      </NavLink>
                      <ArrowIcon
                        className={`${
                          language === "ar"
                            ? "mr-auto group-data-[state=open]/collapsible:-rotate-90"
                            : "ml-auto group-data-[state=open]/collapsible:rotate-90"
                        } transition-transform duration-200`}
                      />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {categories.length !== 0 ? (
                        categories.map((category, index) => {
                          // Filter levels for the current course
                          const categorySubCategories = subCategories.filter(
                            (sub) => sub.category.id === category.id
                          );

                          return (
                            <Collapsible
                              asChild
                              className="group/collapsible"
                              key={index}
                            >
                              <SidebarMenuItem>
                                <CollapsibleTrigger asChild>
                                  <SidebarMenuButton
                                    tooltip={
                                      category.title?.[language] ||
                                      category.title?.en ||
                                      category.title?.ar ||
                                      ""
                                    }
                                  >
                                    <NavHashLink
                                      smooth
                                      to={`/admin/category-view/${category.id}#category`}
                                      state={{ item: category }}
                                      className={`flex items-center gap-2 !bg-transparent truncate`}
                                    >
                                      <Box className="size-4" />
                                      <span>
                                        {category.title?.[language] ||
                                          category.title?.en ||
                                          category.title?.ar ||
                                          ""}
                                      </span>
                                    </NavHashLink>
                                    <ArrowIcon
                                      className={`${
                                        language === "ar"
                                          ? "mr-auto group-data-[state=open]/collapsible:-rotate-90"
                                          : "ml-auto group-data-[state=open]/collapsible:rotate-90"
                                      } transition-transform duration-200`}
                                    />
                                  </SidebarMenuButton>
                                </CollapsibleTrigger>

                                <CollapsibleContent>
                                  <SidebarMenuSub>
                                    {categorySubCategories.length !== 0 ? (
                                      categorySubCategories.map(
                                        (subCat, index) => (
                                          <SidebarMenuSubItem
                                            key={index}
                                            tooltip={
                                              subCat.title?.[language] ||
                                              subCat.title?.en ||
                                              subCat.title?.ar ||
                                              ""
                                            }
                                          >
                                            <SidebarMenuSubButton asChild>
                                              <NavHashLink
                                                smooth
                                                to={`/admin/sub-category-view/${subCat.id}#sub-category`}
                                                state={{
                                                  item: subCat,
                                                  category,
                                                }}
                                                className={`truncate`}
                                              >
                                                <span>
                                                  {subCat.title?.[language] ||
                                                    subCat.title?.en ||
                                                    subCat.title?.ar ||
                                                    ""}
                                                </span>
                                              </NavHashLink>
                                            </SidebarMenuSubButton>
                                          </SidebarMenuSubItem>
                                        )
                                      )
                                    ) : (
                                      <SidebarMenuSubButton className="h-full">
                                        {t.noSubCategory}
                                      </SidebarMenuSubButton>
                                    )}
                                  </SidebarMenuSub>
                                </CollapsibleContent>
                              </SidebarMenuItem>
                            </Collapsible>
                          );
                        })
                      ) : (
                        <SidebarMenuSubButton className="h-full">
                          {t.noCategory}
                        </SidebarMenuSubButton>
                      )}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
              {/* ------------------------------------ */}
              {/* ----------Courses----------- */}
              <Collapsible asChild className="group/collapsible">
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip={t.courses}>
                      <p className={"flex items-center gap-2 !bg-transparent"}>
                        <GraduationCap className="size-4" />
                        <span>{t.courses}</span>
                      </p>
                      <ArrowIcon
                        className={`${
                          language === "ar"
                            ? "mr-auto group-data-[state=open]/collapsible:-rotate-90"
                            : "ml-auto group-data-[state=open]/collapsible:rotate-90"
                        } transition-transform duration-200`}
                      />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {courses.length !== 0 ? (
                        courses.map((course, index) => {
                          return (
                            <Collapsible
                              asChild
                              className="group/collapsible"
                              key={index}
                            >
                              <SidebarMenuItem>
                                <CollapsibleTrigger asChild>
                                  <SidebarMenuButton
                                    tooltip={
                                      course.title?.[language] ||
                                      course.title?.en ||
                                      course.title?.ar ||
                                      ""
                                    }
                                  >
                                    <NavHashLink
                                      smooth
                                      to={`/admin/course-view/${course.id}#course`}
                                      state={{ item: course }}
                                      className={`flex items-center gap-2 !bg-transparent truncate`}
                                    >
                                      <GraduationCap className="size-4" />
                                      <span>
                                        {course.title?.[language] ||
                                          course.title?.en ||
                                          course.title?.ar ||
                                          ""}
                                      </span>
                                    </NavHashLink>
                                    <ArrowIcon
                                      className={`${
                                        language === "ar"
                                          ? "mr-auto group-data-[state=open]/collapsible:-rotate-90"
                                          : "ml-auto group-data-[state=open]/collapsible:rotate-90"
                                      } transition-transform duration-200`}
                                    />
                                  </SidebarMenuButton>
                                </CollapsibleTrigger>

                                <CollapsibleContent>
                                  <SidebarMenuSub>
                                    {course?.levels.length !== 0 ? (
                                      course?.levels.map(
                                        (level, levelIndex) => (
                                          <SidebarMenuSubItem
                                            key={levelIndex}
                                            tooltip={
                                              level.title?.[language] ||
                                              level.title?.en ||
                                              level.title?.ar ||
                                              ""
                                            }
                                          >
                                            <SidebarMenuSubButton asChild>
                                              <HashLink
                                                smooth
                                                to={`/admin/course-view/${course.id}#lessons`}
                                                state={{
                                                  item: course,
                                                  selectedLevelId: level?.id,
                                                }}
                                                className={`flex items-center gap-2 !bg-transparent truncate 
                                                  ${
                                                    storeName ===
                                                      level.title?.en &&
                                                    "!text-blue-600 font-semibold"
                                                  }
                                                  `}
                                                onClick={() =>
                                                  setStoreName(level.title?.en)
                                                }
                                              >
                                                <span>
                                                  {level.title?.[language] ||
                                                    level.title?.en ||
                                                    level.title?.ar ||
                                                    ""}
                                                </span>
                                              </HashLink>
                                            </SidebarMenuSubButton>
                                          </SidebarMenuSubItem>
                                        )
                                      )
                                    ) : (
                                      <SidebarMenuSubButton className="h-full">
                                        {t.noLevel}
                                      </SidebarMenuSubButton>
                                    )}
                                  </SidebarMenuSub>
                                </CollapsibleContent>
                              </SidebarMenuItem>
                            </Collapsible>
                          );
                        })
                      ) : (
                        <SidebarMenuSubButton className="h-full">
                          {t.noCourse}
                        </SidebarMenuSubButton>
                      )}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
              {/* ------------------------------------ */}
              <MenuItemComponent
                Icon={BookOpenText}
                url={"/admin/gradebook"}
                title={"Guidebook"}
                setStoreName={setStoreName}
              />
              <MenuItemComponent
                Icon={Users2}
                url={"/admin/users"}
                title={"Users Management"}
                setStoreName={setStoreName}
              />
              <MenuItemComponent
                Icon={BadgePercent}
                url={"/admin/promo-code"}
                title={"Promo Code"}
                setStoreName={setStoreName}
              />
              <MenuItemComponent
                Icon={MessagesSquare}
                url={"/admin/FAQ"}
                title={"FAQ"}
                setStoreName={setStoreName}
              />
              <MenuItemComponent
                Icon={CreditCard}
                url={"/admin/payment-methods"}
                title={"Payment Methods"}
                setStoreName={setStoreName}
              />
              <MenuItemComponent
                Icon={Wallet2}
                url={"/admin/salary"}
                title={"Salary"}
                setStoreName={setStoreName}
              />
              <MenuItemComponent
                Icon={ArrowRightLeft}
                url={"/admin/transactions"}
                title={"Transactions"}
                setStoreName={setStoreName}
              />
              <MenuItemComponent
                Icon={ListTodo}
                url={"/admin/surveys"}
                title={"Surveys"}
                setStoreName={setStoreName}
              />
              <MenuItemComponent
                Icon={MessageSquareText}
                url={"/admin/chats"}
                title={"Conversations"}
                setStoreName={setStoreName}
              />
              <MenuItemComponent
                Icon={Settings}
                url={"/admin/site-settings"}
                title={"Site Settings"}
                setStoreName={setStoreName}
              />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip={"Logout"}
              className="bg-blue-600 text-white transition-colors hover:bg-blue-600/80 hover:text-white h-10"
              onClick={logout}
            >
              <Link to={"/"}>
                <LogOut />
                <span>{"Logout"}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}

const MenuItemComponent = ({ title, url, Icon, setStoreName }) => (
  <SidebarMenuItem>
    <SidebarMenuButton asChild tooltip={title}>
      <NavLink to={url} onClick={() => setStoreName(title)}>
        <Icon />
        <span>{title}</span>
      </NavLink>
    </SidebarMenuButton>
  </SidebarMenuItem>
);

const CollapsComponent = ({ Icon, title, items, url }) => {
  const { language } = useLanguage();
  const ArrowIcon = language === "ar" ? ChevronLeft : ChevronRight;

  return (
    <Collapsible asChild className="group/collapsible">
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton tooltip={title}>
            <NavLink
              to={url}
              className={"flex items-center gap-2 !bg-transparent"}
            >
              {Icon && <Icon className="size-4" />}
              <span>{title}</span>
            </NavLink>
            <ArrowIcon
              className={`${
                language === "ar"
                  ? "mr-auto group-data-[state=open]/collapsible:-rotate-90"
                  : "ml-auto group-data-[state=open]/collapsible:rotate-90"
              } transition-transform duration-200`}
            />
          </SidebarMenuButton>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <SidebarMenuSub>
            {items.map((item) => (
              <SidebarMenuSubItem key={item.title} tooltip={item.title}>
                {/* <SidebarMenuSubButton asChild>
                  <Link to={item.url}>
                    <span>{item.title}</span>
                  </Link>
                </SidebarMenuSubButton> */}
                <SidebarMenuSubButton asChild>
                  <button onClick={item.url} className="w-full">
                    <span>{item.title}</span>
                  </button>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
};

{
  /* <MenuItemComponent
                Icon={FileText}
                url={"/admi"}
                title={"User Management"}
              />
              <MenuItemComponent
                Icon={GraduationCap}
                url={"/admi"}
                title={"Course Management"}
              />
              <MenuItemComponent
                Icon={BarChart2}
                url={"/admi"}
                title={"Reports & Analytics"}
              />
              <MenuItemComponent
                Icon={Settings}
                url={"/admi"}
                title={"System & Settings Management"}
              />
              <MenuItemComponent
                Icon={Folder}
                url={"/admi"}
                title={"Content Management"}
              />
              <MenuItemComponent
                Icon={Bell}
                url={"/admi"}
                title={"Notifications System"}
              />
              <MenuItemComponent
                Icon={LifeBuoy}
                url={"/admi"}
                title={"Technical Support"}
              />
               <MenuItemComponent
                Icon={CreditCard}
                url={"/admi"}
                title={"Payments & Subscriptions Management"}
              /> */
}
